import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Table } from "react-bootstrap";
import styles from "./PurchaseQuantity.module.css";

function PurchaseQuantity() {
  const [searchValue, setSearchValue] = useState("");
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [bookList, setBookList] = useState([]);
  const [filteredTitles, setFilteredTitles] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const debounceTimeout = useRef(null);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return dd + "/" + mm + "/" + yyyy;
  };

  useEffect(() => {
    const fetchBookList = async () => {
      try {
        const res = await axios.get(INVENTORY_API_BASE_URL + "/getlist");
        setBookList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching book list");
      }
    };

    fetchBookList();
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredTitles([]);
      return;
    }

    const filtered = bookList.filter(
      (item) =>
        item.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchValue.toLowerCase()),
    );

    setFilteredTitles(filtered.slice(0, 10));
  }, [searchValue, bookList]);

  const handleSelect = (item) => {
    setSearchValue(item.sku);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const trimmed = searchValue.trim();
      if (!trimmed) {
        setData([]);
        setError("");
        return;
      }

      setError("");

      let skuToSearch = trimmed;

      const matchedBook = bookList.find(
        (item) => item.title?.toLowerCase() === trimmed.toLowerCase(),
      );

      if (matchedBook) {
        skuToSearch = matchedBook.sku;
      }

      try {
        const response = await axios.get(
          INVENTORY_API_BASE_URL + "/api/purchasequantity",
          { params: { sku: skuToSearch } },
        );

        setData(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setData([]);
        if (err.response?.status === 404) {
          setError("No data found.");
        } else {
          setError("Error fetching data.");
        }
      }
    };

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(fetchData, 500);

    return () => clearTimeout(debounceTimeout.current);
  }, [searchValue, bookList]);

  const totalApprovedQuantity = data.reduce(
    (sum, item) => sum + (Number(item.approvedquantity) || 0),
    0,
  );

  return (
    <main className={styles.container}>
      <div className={styles.workspace}>
        <header className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>Purchasing / Inventory lookup</p>
            <h1>Purchase quantity by SKU</h1>
            <p>Review approved purchase quantities, batch details, and value by book or SKU.</p>
          </div>
          <div className={styles.heroBadge}>
            <span className={styles.heroIcon} aria-hidden="true">SKU</span>
            <span>
              <strong>Purchase history</strong>
              <small>Live inventory records</small>
            </span>
          </div>
        </header>

        <section className={styles.searchPanel} aria-labelledby="purchase-quantity-search-title">
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>Find a book</p>
              <h2 id="purchase-quantity-search-title">Search purchase records</h2>
              <p>Enter a title or SKU. Choose a suggestion to search by its SKU.</p>
            </div>
            <span className={styles.panelTag}>Title or SKU</span>
          </div>

          <div className={styles.searchContent}>
            <label className={styles.searchLabel} htmlFor="purchase-quantity-search">
              Book title or SKU
            </label>
            <div className={styles.searchWrapper}>
              <div className={styles.searchInputShell}>
                <span className={styles.searchIcon} aria-hidden="true">⌕</span>
                <input
                  id="purchase-quantity-search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Search by title or SKU"
                  className={styles.input}
                  autoComplete="off"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestions && filteredTitles.length > 0}
                  aria-controls="purchase-quantity-suggestions"
                />
              </div>

              {showSuggestions && filteredTitles.length > 0 && (
                <ul
                  id="purchase-quantity-suggestions"
                  className={styles.suggestions}
                  role="listbox"
                  aria-label="Book and SKU suggestions"
                >
                  {filteredTitles.map((item) => (
                    <li
                      key={item._id || item.sku}
                      className={styles.suggestionItem}
                      role="option"
                      aria-selected="false"
                      onClick={() => handleSelect(item)}
                    >
                      <strong>{item.title}</strong>
                      <span>SKU {item.sku}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <small className={styles.searchHint}>
              Results update automatically after you type.
            </small>
          </div>
        </section>

        {error && (
          <div className={styles.error} role="alert">
            <span className={styles.errorIcon} aria-hidden="true">!</span>
            <span>{error}</span>
          </div>
        )}

        {data.length > 0 ? (
          <>
            <section className={styles.metrics} aria-label="Purchase quantity summary">
              <article className={styles.metric}>
                <span className={styles.metricIcon} aria-hidden="true">REC</span>
                <span className={styles.metricCopy}>
                  <span>Purchase records</span>
                  <strong>{data.length.toLocaleString()}</strong>
                </span>
              </article>
              <article className={styles.metric + " " + styles.metricQuantity}>
                <span className={styles.metricIcon} aria-hidden="true">QTY</span>
                <span className={styles.metricCopy}>
                  <span>Total approved quantity</span>
                  <strong>{totalApprovedQuantity.toLocaleString("en-IN")}</strong>
                </span>
              </article>
              <article className={styles.metric + " " + styles.metricQuery}>
                <span className={styles.metricIcon} aria-hidden="true">SKU</span>
                <span className={styles.metricCopy}>
                  <span>Current search</span>
                  <strong className={styles.metricQueryValue}>{searchValue}</strong>
                </span>
              </article>
            </section>

            <section className={styles.resultsPanel} aria-labelledby="purchase-quantity-results-title">
              <div className={styles.panelHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>Purchase register</p>
                  <h2 id="purchase-quantity-results-title">Purchase history</h2>
                  <p>Batch-level quantities and purchase values for the selected book.</p>
                </div>
                <span className={styles.recordCount}>
                  {data.length} {data.length === 1 ? "RECORD" : "RECORDS"}
                </span>
              </div>

              <div className={styles.tableWrapper}>
                <Table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Batch</th>
                      <th scope="col">Title</th>
                      <th scope="col">Printing price</th>
                      <th scope="col">Quantity</th>
                      <th scope="col">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => {
                      const price = Number(item.purchasePrice) || 0;
                      const qty = Number(item.approvedquantity) || 0;
                      const value = price * qty;

                      return (
                        <tr key={item._id || index}>
                          <td data-label="Date">{formatDate(item.date) || "—"}</td>
                          <td data-label="Batch">
                            <span className={styles.batchId}>{item.batchid || "—"}</span>
                          </td>
                          <td data-label="Title" className={styles.titleCell}>{item.title || "—"}</td>
                          <td data-label="Printing price">{price}</td>
                          <td data-label="Quantity">
                            <span className={styles.quantity}>{qty}</span>
                          </td>
                          <td data-label="Value">
                            <strong className={styles.value}>{value}</strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <footer className={styles.panelFooter}>
                Showing {data.length} {data.length === 1 ? "purchase record" : "purchase records"}
              </footer>
            </section>
          </>
        ) : !searchValue.trim() && !error ? (
          <section className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">SKU</span>
            <strong>Search to view purchase history</strong>
            <span>Start with a book title or SKU to see batch quantities and purchase values.</span>
          </section>
        ) : null}
      </div>
    </main>
  );
}

export default PurchaseQuantity;
